import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Auth } from '@angular/fire/auth';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from '@angular/fire/storage';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

export interface InvoiceData {
  totalAmount: number;
  taxId: string;
  invoiceDate?: string;
  companyName?: string;
}

export interface Invoice {
  invoiceId: string;
  userId: string;
  totalAmount: number;
  taxId: string;
  companyName?: string;
  invoiceDate?: string;
  downloadUrl: string;
  storagePath: string;
  xpAwarded: number;
  createdAt: string;
  status: 'processing' | 'completed' | 'failed';
}

export interface ProcessInvoiceResponse {
  success: boolean;
  invoice?: Invoice;
  error?: {
    code: string;
    message: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  private auth = inject(Auth);
  private storage = inject(Storage);
  private http = inject(HttpClient);

  /**
   * Upload invoice file to Firebase Storage temporary folder
   * @param file - The PDF or image file from the scanner
   * @param userId - Current user ID
   * @returns Object with tempPath and downloadUrl
   */
  async uploadToTempStorage(
    file: Blob,
    userId: string
  ): Promise<{ tempPath: string; downloadUrl: string }> {
    // Generate unique filename
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(7);
    const extension = file.type.includes('pdf') ? 'pdf' : 'jpg';
    const filename = `${timestamp}_${randomId}.${extension}`;

    // Create temp storage path
    const tempPath = `invoices/tmp/${userId}/${filename}`;
    const storageRef = ref(this.storage, tempPath);

    // Upload file
    await uploadBytes(storageRef, file);

    // Get download URL
    const downloadUrl = await getDownloadURL(storageRef);

    return { tempPath, downloadUrl };
  }

  /**
   * Process invoice by calling backend API
   * This triggers OCR, LLM extraction, duplicate detection, and XP award
   * @param tempPath - Temporary storage path
   * @param downloadUrl - Firebase Storage download URL
   * @returns Processed invoice data
   */
  async processInvoice(
    tempPath: string,
    downloadUrl: string
  ): Promise<ProcessInvoiceResponse> {
    try {
      // Get Firebase ID token for authentication
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('Processing invoice:', { tempPath, apiUrl: environment.invoiceProcess });

      const response = await firstValueFrom(
        this.http.post<ProcessInvoiceResponse>(
          `${environment.invoiceProcess}/invoice/process`,
          {
            tempPath,
            downloadUrl,
          }
        )
      );

      return response;
    } catch (error: any) {
      console.error('Invoice processing API error:', error);

      // Log the full error structure for debugging
      console.log('Full error object:', error);
      console.log('error.error:', error.error);
      console.log('error.message:', error.message);
      console.log('error.status:', error.status);

      // Extract meaningful error message
      if (error.error) {
        // Backend returned an error response
        // The structure from backend is: { success: false, error: { code, message } }
        // But HttpErrorResponse wraps it, so we access via error.error

        let errorMessage = 'Backend-Fehler';
        let errorCode = 'unknown';

        // Check if error.error has the expected structure
        if (error.error.error?.message) {
          // Nested: error.error.error.message
          errorMessage = error.error.error.message;
          errorCode = error.error.error.code || 'unknown';
        } else if (error.error.message) {
          // Direct: error.error.message
          errorMessage = error.error.message;
          errorCode = error.error.code || 'unknown';
        } else if (typeof error.error === 'string') {
          // Plain string error
          errorMessage = error.error;
        }

        console.log('Extracted error message:', errorMessage);
        console.log('Extracted error code:', errorCode);

        throw {
          message: errorMessage,
          code: errorCode,
          error: error.error
        };
      } else if (error.status === 0) {
        // Network error or CORS issue
        throw {
          message: 'Netzwerkfehler - Backend nicht erreichbar',
          code: 'network-error',
          error
        };
      } else {
        // Other error
        throw {
          message: error.message || 'Unbekannter Fehler',
          code: 'unknown',
          error
        };
      }
    }
  }

  /**
   * Get all invoices for the current user
   * @returns List of user's invoices
   */
  async getUserInvoices(): Promise<Invoice[]> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const idToken = await user.getIdToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${idToken}`,
    });

    const response = await firstValueFrom(
      this.http.get<{ invoices: Invoice[] }>(
        `${environment.apiUrl}/experience/invoices`,
        { headers }
      )
    );

    return response.invoices;
  }

  /**
   * Delete an invoice
   * @param invoiceId - ID of invoice to delete
   */
  async deleteInvoice(invoiceId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const idToken = await user.getIdToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${idToken}`,
    });

    await firstValueFrom(
      this.http.delete(
        `${environment.apiUrl}/experience/invoices/${invoiceId}`,
        { headers }
      )
    );
  }

  /**
   * Clean up temporary file if upload or processing fails
   * @param tempPath - Path to temporary file
   */
  async cleanupTempFile(tempPath: string): Promise<void> {
    try {
      const storageRef = ref(this.storage, tempPath);
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Failed to cleanup temp file:', error);
      // Don't throw - this is cleanup, not critical
    }
  }
}
